// Installation settings (SCR-156): per installation, whether its proxy runs what the settings say.
// An overview of every installation (applied, drift, needs redeploy, last known good only), and
// under the scope selector one installation's detail: the applied state per task, the fields its
// deployment pins with the parameter that pins each, drift, and the fields waiting on a redeploy.
// Read-only: the values are edited in the customer's settings tabs.
import { useEffect, useState } from "preact/hooks";
import type { ApiClient } from "@nexus/ui";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { ScopeSelector, type ScopeOption } from "@nexus/ui/settings/scope-selector";
import type { HelpId } from "../../help/topics";
import { Card, Chip } from "@nexus/ui";
import { Empty, ErrorBanner, Loading } from "@nexus/ui";
import { formatDate } from "./model";
import {
  describeInstallation,
  sortOverview,
  type InstallationSettingsView,
  type OverviewRow,
  type PointerRow,
} from "./installation-settings-model";

const PINS_HELP: HelpId = "settings.pins";
const DRIFT_HELP: HelpId = "settings.drift";
const REDEPLOY_HELP: HelpId = "settings.needsRedeploy";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** At most this many of the per-installation reads at once: a session may make 20 requests a
 *  second (burst 40), and a customer with many installations — every installer run adds one —
 *  otherwise sees "rate limit exceeded" on the overview (TASK-195, FLOW-013). */
const OVERVIEW_CONCURRENCY = 4;

/** One overview row per installation: its layers read, or "could not load". */
async function loadOverview(client: ApiClient, orgId: string, items: readonly { id: string; name: string }[]): Promise<OverviewRow[]> {
  const queue = [...items];
  const rows: OverviewRow[] = [];
  const worker = async () => {
    for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
      try {
        const layers = await client.get("/v1/orgs/{orgId}/installations/{installationId}/settings", { path: { orgId, installationId: item.id } });
        rows.push({ id: item.id, name: item.name, view: describeInstallation(layers), failed: false });
      } catch {
        rows.push({ id: item.id, name: item.name, view: null, failed: true });
      }
    }
  };
  await Promise.all(Array.from({ length: OVERVIEW_CONCURRENCY }, worker));
  return rows;
}

export function InstallationSettings({ orgId }: { orgId: string }) {
  const client = useApi();
  const [rows, setRows] = useState<OverviewRow[] | null>(null);
  const [scope, setScope] = useState("");
  const [error, setError] = useState<ApiError | null>(null);

  const load = () => {
    setError(null);
    setRows(null);
    client
      .get("/v1/orgs/{orgId}/installations", { path: { orgId }, query: { limit: 500 } })
      .then((page) => loadOverview(client, orgId, page.items))
      .then((loaded) => setRows(sortOverview(loaded)))
      .catch((err: unknown) => setError(unexpected(err)));
  };
  useEffect(load, [client, orgId]);

  if (error) return <ErrorBanner error={error} onRetry={load} />;
  if (!rows) return <Loading label="Loading installation settings…" />;
  if (rows.length === 0) return <Empty message="This customer has no installations yet." />;

  const options: ScopeOption[] = rows.map((row) => ({ id: row.id, label: row.name }));
  const selected = rows.find((row) => row.id === scope) ?? null;

  return (
    <>
      <Card title="Installations">
        <ul class="settings-overview" aria-label="Installation settings status">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" class="link" aria-pressed={row.id === scope} onClick={() => setScope(row.id)}>
                {row.name}
              </button>{" "}
              {row.view ? <Chip tone={row.view.tone}>{row.view.healthLabel}</Chip> : <Chip tone="red">Could not load</Chip>}
            </li>
          ))}
        </ul>
      </Card>
      <ScopeSelector installations={options} value={scope} onChange={setScope} />
      {selected ? <Detail row={selected} /> : <Empty message="The org default applies to every installation that does not override it. Choose an installation to see what its proxy runs." />}
    </>
  );
}

function Detail({ row }: { row: OverviewRow }) {
  if (!row.view) return <Empty message={`The settings for ${row.name} could not be loaded. Reload the page to try again.`} />;
  const view = row.view;
  return (
    <>
      <Card title={row.name} actions={<Chip tone={view.tone}>{view.healthLabel}</Chip>}>
        <p class="card-note">{view.summary}</p>
        {view.appliedAt && <p class="card-note">Applied {formatDate(view.appliedAt)}, last reported {view.appliedAt.slice(11, 16)} UTC.</p>}
        <p class="card-note">{overrideLine(view)}</p>
      </Card>
      <TaskList view={view} />
      <Pointers title="Pinned by the deployment" help={PINS_HELP} empty="The deployment pins nothing; every field can be changed here." rows={view.pins.map((pin) => ({ pointer: pin.path, label: pin.label, parameter: pin.parameter }))} />
      <Pointers title="Drift" help={DRIFT_HELP} empty="No task's pinned values disagree with the stored override." rows={view.drift} />
      <Pointers title="Needs redeploy" help={REDEPLOY_HELP} empty="Nothing waits on a redeploy." rows={view.needsRedeploy} />
    </>
  );
}

function overrideLine(view: InstallationSettingsView): string {
  // A stored override has a version ≥ 1; the API carries its author and time beside it.
  if (view.overrideVersion < 1) return "No override is stored; the org default applies in full.";
  const when = view.overrideUpdatedAt ? ` saved ${formatDate(view.overrideUpdatedAt)}` : "";
  const who = view.overrideUpdatedBy ? ` by ${view.overrideUpdatedBy}` : "";
  return `Override version ${view.overrideVersion},${when}${who}.`.replace(",.", ".");
}

function TaskList({ view }: { view: InstallationSettingsView }) {
  return (
    <Card title="Tasks">
      {view.tasks.length === 0 ? (
        <p class="card-note">No task has reported yet, so nothing is confirmed as applied.</p>
      ) : (
        <ul class="settings-tasks">
          {view.tasks.map((task) => (
            <li key={task.taskId}>
              <Chip tone={task.tone}>{task.stateLabel}</Chip> <code>{task.taskId}</code> · plugin {task.pluginVersion} · digest <code>{task.digest.slice(0, 12)}</code> · {formatDate(task.reportedAt)} {task.reportedAt.slice(11, 16)} UTC
              {task.warning && (
                <p class="card-note" role="status">
                  {task.warning}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Pointers({ title, help, empty, rows }: { title: string; help: HelpId; empty: string; rows: readonly (PointerRow | { pointer: string; label: string; parameter: string | null })[] }) {
  return (
    <Card title={title} help={help}>
      {rows.length === 0 ? (
        <p class="card-note">{empty}</p>
      ) : (
        <ul class="settings-pointers">
          {rows.map((row) => (
            <li key={row.pointer}>
              {row.label}
              {row.parameter && (
                <>
                  {" "}
                  <code>{row.parameter}</code>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
